/**
 * Role categorization utilities for separating regular access roles from content-based roles
 * This is a frontend-only solution that doesn't change the backend
 */

export interface Role {
    id: number;
    name: string;
    description?: string;
    user_count: number;
    permission_count: number;
    created_on: string | null;
    updated_on: string | null;
    platform_managed?: boolean;
}

export type RoleCategory = 'access' | 'content';

export interface CategorizedRole extends Role {
    category: RoleCategory;
    isPlatformManaged: boolean;
}

// Regular access roles - these are business logic roles
export const ACCESS_ROLES = [
    'Admin',
    'Manager', 
    'Analyst',
    'Api',
];

// Content-based role patterns - these provide access to specific content
export const CONTENT_ROLE_PATTERNS = [
    /^Channel\s+/i,     // "Channel ChannelName"
    /^Team\s+/i,        // "Team TeamName"  
    /^Agent\s+/i        // "Agent AgentName"
];


/**
 * Determine if a role name matches content-based role patterns
 */
export function isContentRole(roleName: string): boolean {
    return CONTENT_ROLE_PATTERNS.some(pattern => pattern.test(roleName));
}

/**
 * Determine if a role is a regular access role
 */
export function isAccessRole(roleName: string): boolean {
    return ACCESS_ROLES.includes(roleName);
}

/**
 * Determine if a role is platform managed (shouldn't be deleted/edited)
 */
export function isPlatformManagedRole(role: Role): boolean {
    return role.platform_managed === true;
}

/**
 * Categorize a single role
 */
export function categorizeRole(role: Role): CategorizedRole {
    const category: RoleCategory = isContentRole(role.name) ? 'content' : 'access';
    const isPlatformManagedFlag = isPlatformManagedRole(role);
    
    return {
        ...role,
        category,
        isPlatformManaged: isPlatformManagedFlag
    };
}

/**
 * Categorize an array of roles
 */
export function categorizeRoles(roles: Role[]): CategorizedRole[] {
    return roles.map(categorizeRole);
}

/**
 * Filter roles by category
 */
export function filterRolesByCategory(roles: Role[], category: RoleCategory): CategorizedRole[] {
    const categorized = categorizeRoles(roles);
    return categorized.filter(role => role.category === category);
}

/**
 * Get access roles only
 */
export function getAccessRoles(roles: Role[]): CategorizedRole[] {
    return filterRolesByCategory(roles, 'access');
}

/**
 * Get content roles only
 */
export function getContentRoles(roles: Role[]): CategorizedRole[] {
    return filterRolesByCategory(roles, 'content');
}

/**
 * Parse content role to extract type and name
 */
export function parseContentRole(roleName: string): { type: string; name: string } | null {
    for (const pattern of CONTENT_ROLE_PATTERNS) {
        const match = roleName.match(pattern);
        if (match) {
            const type = match[0].trim().toLowerCase(); // "channel", "team", "agent"
            const name = roleName.replace(pattern, '').trim();
            return {type, name};
        }
    }
    return null;
}

/**
 * Group content roles by type
 */
export function groupContentRolesByType(roles: Role[]): Record<string, CategorizedRole[]> {
    const contentRoles = getContentRoles(roles);
    const grouped: Record<string, CategorizedRole[]> = {
        channel: [],
        team: [],
        agent: []
    };
    
    contentRoles.forEach(role => {
        const parsed = parseContentRole(role.name);
        if (parsed && grouped[parsed.type]) {
            grouped[parsed.type].push(role);
        }
    });
    
    return grouped;
}

/**
 * Get role category display information
 */
export function getRoleCategoryInfo(category: RoleCategory) {
    const info = {
        access: {
            title: 'Access Roles',
            description: 'Business logic roles that define what users can do in the system',
            icon: 'user-shield',
            color: 'primary'
        },
        content: {
            title: 'Content Access Roles', 
            description: 'Roles that provide access to specific channels, teams, or agents',
            icon: 'folder-open',
            color: 'info'
        }
    };
    
    return info[category];
}

/**
 * Get content type display information
 */
export function getContentTypeInfo(type: string) {
    const info: Record<string, {
        title: string;
        description: string;
        icon: string;
        color: string;
    }> = {
        channel: {
            title: 'Channel Access',
            description: 'Access to specific channels',
            icon: 'broadcast-tower',
            color: 'success'
        },
        team: {
            title: 'Team Access',
            description: 'Access to specific teams',
            icon: 'users',
            color: 'warning'
        },
        agent: {
            title: 'Agent Access', 
            description: 'Access to specific agents',
            icon: 'user',
            color: 'info'
        }
    };
    
    return info[type] || {
        title: 'Unknown',
        description: 'Unknown content type',
        icon: 'question',
        color: 'secondary'
    };
}
